import noop from 'lodash/noop';
import React, { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { APP_URLS, xTenantId } from '../../../consts';
import { useSnackbar } from '../../../hooks/snackbar';
import { useUserContext } from '../../../providers/UserProvider';
import { HowAccountReviewWorkModal } from './HowItWorksModal/HowAccountReviewWorkModal';
import { AccountReviewEditModal, AccountReviewFormData } from './AccountReviewEditModal/AccountReviewEditModal';
import { AccountReviewSection } from './AccountReviewSection/AccountReviewSection';
import './AccountReview.scss';
import { useTranslations } from '../../../hooks/useTranslationsHelper';
import { useUserProfileUpdateMutation } from '../../../libs/graphql/baseAppAPI/mutations/__generated__/user-profile-update.mutation.generated';

type AccountReviewProps = {
  isModal: boolean;
  loading?: boolean;
  onClose?: () => void;
  finallyReturnToGamblingControl?: boolean;
};

export const AccountReview: FC<AccountReviewProps> = ({
  isModal,
  loading = false,
  onClose = noop,
  finallyReturnToGamblingControl = true,
}) => {
  const { t } = useTranslations();
  const { addSnack } = useSnackbar();
  const navigate = useNavigate();
  const { consents, userProfileId, fetchUserProfile } = useUserContext();

  const interval = consents?.lastAccountReviewInterval;

  const [displayEditModal, setDisplayEditModal] = useState<boolean>(false);
  const [displayHowItWorks, setHowItWorks] = useState<boolean>(false);
  const [intervalWithLabel, setIntervalWithLabel] = useState<string>('');

  const [userProfileUpdateMutation, { loading: saveLoading }] = useUserProfileUpdateMutation({
    onCompleted: async (data) => {
      if (!data) return;
      await fetchUserProfile();
      addSnack({
        type: 'success',
        theme: 'dark',
        message: t('account-review.updated.text'),
      });
    },
    onError: (error) => {
      console.error('Error updating profile:', error);
      addSnack({
        type: 'error',
        theme: 'dark',
        message: t('errors.try-later'),
      });
    },
  });

  const onEditModalClose = (): void => setDisplayEditModal(false);

  useEffect(() => {
    const editParam = new URLSearchParams(window.location.search).get('edit');

    if (editParam === 'account-review') {
      setTimeout(() => {
        setDisplayEditModal(true);

        const url = new URL(window.location.href);

        window.history.pushState({}, document.title, url.pathname);
      }, 500);
    }
  }, [window.location.search]);

  useEffect(() => {
    isModal && setDisplayEditModal(true);
  }, [isModal]);

  useEffect(() => {
    setIntervalWithLabel(interval || '');
  }, [interval]);

  const onFormSubmit = async (formData: AccountReviewFormData): Promise<void> => {
    await userProfileUpdateMutation({
      variables: {
        userProfileId,
        xTenantId,
        userProfileUpdateInfo: {
          lastAccountReviewInterval: formData.interval,
        },
      },
    });

    onEditModalClose();
  };

  const onCloseHowItWorks = (): void => {
    setHowItWorks(false);
  };

  const onCloseEdit = (): void => {
    onClose();
    onEditModalClose();
    finallyReturnToGamblingControl && navigate(APP_URLS.myAccount.gamblingControls);
  };

  const onCancelEdit = (): void => {
    onEditModalClose();
    onClose();
    finallyReturnToGamblingControl && navigate(APP_URLS.myAccount.gamblingControls);
  };

  const onSubmitEdit = async (formData: AccountReviewFormData): Promise<void> => {
    await onFormSubmit(formData);
    onClose();
    finallyReturnToGamblingControl && navigate(APP_URLS.myAccount.gamblingControls);
  };

  return (
    <div className="account-review">
      {!isModal && (
        <div>
          <AccountReviewSection
            intervalWithLabel={intervalWithLabel}
            openHowItWorksModal={(): void => setHowItWorks(true)}
            openEditModal={(): void => {
              setDisplayEditModal(true);
            }}
            loading={loading}
          />
          <HowAccountReviewWorkModal isOpen={displayHowItWorks} onClose={onCloseHowItWorks} />
        </div>
      )}
      <AccountReviewEditModal
        isOpen={displayEditModal}
        intervalWithLabel={intervalWithLabel}
        onClose={onCloseEdit}
        onFormSubmit={onSubmitEdit}
        onCancel={onCancelEdit}
        loading={loading || saveLoading}
      />
    </div>
  );
};
